// src/pages/auth/ForgotPasswordPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import authApi from '../../api/authApi';
import Logo from '../../assets/logo1.png'; // Adjust path
import LoginBg from '../../assets/login-bg.jpg';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailNotFound, setEmailNotFound] = useState(false);

  // const validateIdentifier = (value) => {
  //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  //   const codeRegex = /^[A-Z0-9]+$/;
  //   return emailRegex.test(value) || codeRegex.test(value);
  // };

  const validateIdentifier = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const codeRegex = /^[A-Z0-9-]+$/; // allow uppercase letters, digits, and hyphens
    return emailRegex.test(value) || codeRegex.test(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!identifier.trim()) {
      setError('Email is required');
      return;
    }

    // if (!validateIdentifier(identifier)) {
    //   setError('Please enter a valid email or code');
    //   return;
    // }

    setIsLoading(true);
    setError('');
    setEmailNotFound(false);

    try {
      // 1. Call the API
      const response = await authApi.requestPasswordReset(identifier);

      // ---------------------------------------------------------
      // 2. CRITICAL FIX: Check if API explicitly returned failure
      // ---------------------------------------------------------
      // If backend returns HTTP 200 but success: false, we must handle it here
      if (response && response.success === false) {
        // Manually throw an error object that mimics an Axios error
        // so our existing catch block handles the UI updates correctly
        throw {
          response: {
            data: response
          }
        };
      }

      // 3. If we get here, success is TRUE
      setIsLoading(false);
      setEmailSent(true);
      toast.success('Password reset link sent! Check your email.');

    } catch (err) {
      setIsLoading(false);

      // --- ERROR HANDLING ---

      const isHttp404 = err.response?.status === 404;
      const isBody404 = err.response?.data?.data === 404; // Your API returns data: 404
      const isMsgNotFound = err.response?.data?.message?.toLowerCase().includes('not found');

      const isNotFound = isHttp404 || isBody404 || isMsgNotFound;

      const msg = err.response?.data?.message || err.message || 'Failed to send reset link.';

      // Show error message on screen
      setError(msg);

      // Show Toast
      toast.error(msg);

      if (isNotFound) {
        setEmailNotFound(true);
      }
    }
  };

  const handleLoginAnotherWay = () => {
    navigate('/security-question-login');
  };

  /* -------------------------- EMAIL SENT VIEW -------------------------- */
  if (emailSent) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 p-4">
        {/* Laptops and large screens: the same watercolour as the login page. Tablets and
            phones keep the plain background (they use the mobile app). */}
        <div aria-hidden="true" className="hidden lg:block absolute inset-0 bg-[#fdf1dc] bg-cover bg-center"
          style={{ backgroundImage: `url(${LoginBg})` }} />
        <div aria-hidden="true" className="hidden lg:block absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 50% 42%, rgba(255,255,255,.55), rgba(255,255,255,0) 62%)' }} />
        <div className="relative w-full max-w-md">
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-flex items-center justify-center w-full mx-auto rounded-2xl bg-transparent mb-4 p-2">
              <img src={Logo} alt="Logo" className="w-full h-full object-contain lg:max-w-[170px] lg:mx-auto" />
            </div>
          </div>

          <Card className="shadow-xl animate-slide-in lg:rounded-[20px] lg:border lg:border-white/90 lg:bg-white/70 lg:backdrop-blur-md lg:shadow-[0_18px_50px_rgba(120,80,30,0.16)]">
            <CardHeader>
              <CardTitle className="text-2xl text-center">Email Sent!</CardTitle>
              <CardDescription className="text-center">
                We've sent a reset link to <span className="font-semibold">{identifier}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                Click the link in your email to create a new password.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full">
                Back to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  /* -------------------------- MAIN FORM UI -------------------------- */
  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-muted/30">
      {/* Laptops and large screens: the same watercolour as the login page. Tablets and
          phones keep the plain background (they use the mobile app). */}
      <div aria-hidden="true" className="hidden lg:block absolute inset-0 bg-[#fdf1dc] bg-cover bg-center"
        style={{ backgroundImage: `url(${LoginBg})` }} />
      <div aria-hidden="true" className="hidden lg:block absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at 50% 42%, rgba(255,255,255,.55), rgba(255,255,255,0) 62%)' }} />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <img src={Logo} alt="Mohilya" className="hidden lg:block mx-auto max-w-[150px] h-auto mb-3" />
          <h1 className="text-3xl font-bold font-['Space_Grotesk'] mb-2 text-white lg:text-[26px] lg:font-normal lg:tracking-wide lg:text-[#5b1e1e] lg:[font-family:Marcellus,Georgia,'Times_New_Roman',serif]">Forgot Password?</h1>
          <p className="text-muted-foreground text-white lg:text-[13px] lg:text-[#8a7767]">No worries, we'll send you reset instructions.</p>
        </div>

        <Card className="shadow-xl animate-slide-in lg:rounded-[20px] lg:border lg:border-white/90 lg:bg-white/70 lg:backdrop-blur-md lg:shadow-[0_18px_50px_rgba(120,80,30,0.16)]">
          <CardHeader>
            <CardTitle className="text-2xl flex justify-center">Reset Password</CardTitle>
            {/* <CardDescription>
              Enter your email
            </CardDescription> */}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="identifier">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="identifier"
                    type="email"
                    placeholder="name@company.com"
                    value={identifier}
                    onChange={(e) => {
                      setIdentifier(e.target.value);
                      setError('');
                      setEmailNotFound(false);
                    }}
                    className={cn('pl-10', error && 'border-destructive')}
                  />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Checking...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              {/* Manual fallback button (if you prefer clicking over auto-redirect) */}
              {/* {!emailNotFound && ( */}
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">Don't have Email? Try verifying with security questions instead.</p>
                  <Button
                    // type="button"
                    // variant="outline"
                    className="w-full"
                    onClick={handleLoginAnotherWay}
                    // className= ""
                  // onClick={() => navigate('/security-question-login')}
                  >
                    Try Security Questions
                  </Button>
                </div>
              {/* )} */}

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;